<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatEur } from '@/composables/useSubscription'
import { useSheetScrollLock } from '@/composables/useSheetScrollLock'

/**
 * The recommended-bundles results screen, rendered generically from a normalized
 * card list. The parent resolves each card (name, price, image, detail) from
 * whatever source applies to the product (the backend flow or, until wired,
 * front data) and owns the selection navigation via the `select` event.
 */

type DetailRow = { label: string; value: string }
export type ResultDetail = {
  summary: string
  rows: DetailRow[]
  /** Calaméo guarantee sheet for the formula, or null/absent when the product
   *  has no published sheet — the link is then omitted rather than pointing at
   *  a neighbouring document (SI-1455). */
  sheetUrl?: string | null
}

export type ResultCard = {
  /** Plan slug used for selection + as the v-for key. */
  key: string
  name: string
  /** Optional description line (under the name). */
  description?: string
  category: string
  image: string
  imageAlt: string
  recommended: boolean
  /** Monthly price in euros, or null to hide the price block. */
  priceMonth: number | null
  /** Yearly price in euros, or null to hide the per-year line. */
  priceYear: number | null
  /** "En savoir +" content, or null when none. */
  detail: ResultDetail | null
}

const props = defineProps<{
  title: string
  cards: ResultCard[]
  loading?: boolean
  /** i18n key of a load error, or null. */
  errorKey?: string | null
  /** True on small screens: the detail panel becomes a teleported bottom sheet. */
  isMobileSheet?: boolean
}>()

defineEmits<{ select: [key: string]; retry: [] }>()

const { t, locale } = useI18n()

/** One "En savoir +" panel open at a time. */
const openDetail = ref<string | null>(null)
const detailEl = ref<HTMLElement | null>(null)

async function toggleDetail(key: string): Promise<void> {
  const opening = openDetail.value !== key
  openDetail.value = opening ? key : null
  // On desktop the panel unfolds below the cards, often past the fold: bring the
  // guarantee table into view once it is rendered. The mobile sheet is already
  // pinned to the viewport, so it needs no scroll.
  if (!opening || props.isMobileSheet) return
  await nextTick()
  detailEl.value?.scrollIntoView({
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    block: 'start',
  })
}
const openCard = computed(() => props.cards.find((c) => c.key === openDetail.value) ?? null)

/**
 * Card width adapts to how many bundles came back: a lone card stays narrow,
 * two cards cap at two columns, three or more use the full responsive grid.
 */
const gridClass = computed(() => {
  if (props.cards.length <= 1) return 'journey-results__grid--single'
  if (props.cards.length === 2) return 'journey-results__grid--duo'
  return null
})

// On mobile, lock body scroll + close on Escape while the detail sheet is open.
useSheetScrollLock(
  computed(() => !!props.isMobileSheet && openDetail.value !== null),
  () => { openDetail.value = null },
)

function formatPrice(value: number): string {
  return formatEur(value, locale.value)
}
</script>

<template>
  <div class="journey-results">
    <h1 id="journey-question" class="journey-results__title">{{ title }}</h1>

    <p v-if="loading" class="journey-results__status" role="status">
      {{ t('subscription.results.loading') }}
    </p>

    <div
      v-else-if="errorKey"
      class="journey-results__status journey-results__status--error"
      role="alert"
    >
      <p>{{ t(errorKey) }}</p>
      <button type="button" class="journey-results__retry" @click="$emit('retry')">
        {{ t('subscription.results.retry') }}
      </button>
    </div>

    <div v-else class="journey-results__grid" :class="gridClass">
      <article
        v-for="card in cards"
        :key="card.key"
        class="product-result"
        :class="{
          'product-result--recommended': card.recommended,
          'product-result--active': openDetail === card.key,
        }"
      >
        <span v-if="card.recommended" class="product-result__badge">
          <svg viewBox="0 0 20 20" width="12" height="12" aria-hidden="true">
            <path d="M10 2l2.2 4.6 5 .6-3.7 3.4 1 5L10 13.7 5.5 15.6l1-5L2.8 7.2l5-.6z" fill="currentColor"/>
          </svg>
          {{ t('subscription.results.recommended') }}
        </span>

        <div class="product-result__media">
          <img :src="card.image" :alt="card.imageAlt" width="800" height="446" loading="lazy" />
        </div>

        <p class="product-result__category">{{ card.category }}</p>
        <h2 class="product-result__name">{{ card.name }}</h2>
        <p v-if="card.description" class="product-result__desc">{{ card.description }}</p>
        <template v-if="card.priceMonth !== null">
          <p class="product-result__price">
            <span class="product-result__amount">{{ formatPrice(card.priceMonth) }}</span>
            <span class="product-result__period">{{ t('subscription.results.perMonth') }}</span>
          </p>
          <p v-if="card.priceYear !== null" class="product-result__price-year">
            {{ formatPrice(card.priceYear) }}{{ t('subscription.results.perYear') }}
          </p>
        </template>
        <!-- Yearly-only cover (e.g. civil liability): the per-year price is the
             headline, with no monthly figure. -->
        <p v-else-if="card.priceYear !== null" class="product-result__price">
          <span class="product-result__amount">{{ formatPrice(card.priceYear) }}</span>
          <span class="product-result__period">{{ t('subscription.results.perYear') }}</span>
        </p>

        <div class="product-result__actions">
          <button
            type="button"
            class="product-result__choose"
            @click="$emit('select', card.key)"
          >
            {{ t('subscription.results.choose') }}
          </button>
          <button
            type="button"
            class="product-result__more"
            :aria-expanded="openDetail === card.key"
            aria-controls="journey-results-detail"
            @click="toggleDetail(card.key)"
          >
            <span>{{ openDetail === card.key
              ? t('subscription.results.learnLess')
              : t('subscription.results.learnMore') }}</span>
            <svg
              class="product-result__chevron"
              :class="{ 'is-open': openDetail === card.key }"
              viewBox="0 0 20 20" width="14" height="14" aria-hidden="true"
            >
              <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </article>
    </div>

    <Teleport to="body" :disabled="!isMobileSheet">
      <div
        v-if="openCard && openCard.detail"
        class="results-sheet"
        :class="{ 'results-sheet--mobile': isMobileSheet }"
      >
        <div class="results-sheet__backdrop" @click="openDetail = null" />
        <div
          id="journey-results-detail"
          ref="detailEl"
          class="results-detail"
          :role="isMobileSheet ? 'dialog' : undefined"
          :aria-modal="isMobileSheet ? 'true' : undefined"
        >
          <span class="results-detail__grabber" aria-hidden="true" />
          <button
            type="button"
            class="results-detail__close"
            :aria-label="t('subscription.results.learnLess')"
            @click="openDetail = null"
          >
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <p class="results-detail__intro">👉 {{ t('subscription.results.inClear') }}</p>
          <p class="results-detail__summary">
            <strong>{{ openCard.name }}</strong> : {{ openCard.detail.summary }}
          </p>
          <dl class="results-detail__rows">
            <div
              v-for="row in openCard.detail.rows"
              :key="row.label"
              class="results-detail__row"
            >
              <dt>{{ row.label }}</dt>
              <dd>{{ row.value }}</dd>
            </div>
          </dl>
          <!-- Calaméo sheet: an external document, so it opens in a new tab and
               drops the opener reference (rel) to keep the journey state intact
               behind it — losing a half-filled subscription form to a PDF would
               be a real regression. -->
          <a
            v-if="openCard.detail.sheetUrl"
            class="results-detail__sheet"
            :href="openCard.detail.sheetUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ t('subscription.results.guaranteeSheet') }}
            <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
              <path d="M8 4H5v11h11v-3M12 3h5v5M17 3l-7 7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.journey-results {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  /* Référence pour les container queries de la grille : on mesure la largeur
   * réelle de la colonne parcours (amputée de l'aside 17rem), pas le viewport. */
  container-type: inline-size;
}
.journey-results__title {
  font-size: var(--text-xl);
  line-height: var(--leading-tight);
}
/* États de chargement / erreur de la liste des formules (available_bundles). */
.journey-results__status {
  margin-top: 1rem;
  color: var(--text-muted);
}
.journey-results__status--error {
  color: var(--text);
}
.journey-results__retry {
  margin-top: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--bg-surface);
  color: var(--text);
  cursor: pointer;
}
.journey-results__retry:hover {
  background: var(--bg-soft);
}
/* Colonnes pilotées par la largeur du conteneur (@container), pas du viewport :
 * 1 colonne par défaut → 2 dès 30rem de large → 4 quand la place le permet
 * vraiment (≥ 54rem). minmax(0,1fr) autorise les pistes à rétrécir sous le
 * min-content : plus aucun débordement horizontal sur tablette. */
.journey-results__grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
  align-items: stretch;
}
@container (min-width: 30rem) {
  .journey-results__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@container (min-width: 54rem) {
  .journey-results__grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}
/* Variante 2 offres (responsabilité civile) : jamais plus de 2 colonnes, et
 * largeur de carte bornée pour ne pas s'étaler sur grand écran. */
.journey-results__grid--duo {
  max-width: 46rem;
}
@container (min-width: 30rem) {
  .journey-results__grid--duo { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@container (min-width: 54rem) {
  .journey-results__grid--duo { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
/* Variante 1 seule offre (étranger) : carte unique, largeur bornée. */
.journey-results__grid--single {
  grid-template-columns: minmax(0, 24rem);
}

.product-result {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 1rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  transition:
    transform var(--dur-base) var(--ease-spring),
    box-shadow var(--dur-base) var(--ease-out),
    border-color var(--dur-base) var(--ease-out);
}
.product-result:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
}
.product-result--recommended {
  border-color: var(--primary);
  box-shadow: 0 0 0 1px var(--primary), var(--shadow-sm);
  background: color-mix(in oklab, var(--color-peach) 10%, white);
}
.product-result--active {
  border-color: var(--primary);
  box-shadow: 0 0 0 2px var(--primary);
}

.product-result__badge {
  position: absolute;
  top: -0.7rem;
  left: 1.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.7rem;
  background: var(--primary);
  color: var(--text-on-primary);
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-sm);
}
.product-result--recommended .product-result__media { margin-top: 0.4rem; }

.product-result__category {
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}
.product-result__name {
  font-size: var(--text-lg);
  font-weight: 800;
  color: var(--color-chocolate);
}
.product-result__desc {
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--text-muted);
}
.product-result__price {
  display: flex;
  align-items: baseline;
  gap: 0.1rem;
  margin-top: auto;
  padding-top: 0.5rem;
}
.product-result__amount {
  font-size: var(--text-xl);
  font-weight: 800;
  color: var(--color-chocolate);
}
.product-result__period {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-muted);
}
.product-result__price-year {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-muted);
}
.product-result__media {
  overflow: hidden;
  border-radius: var(--radius-md);
  background: color-mix(in oklab, var(--color-peach) 16%, white);
}
.product-result__media img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 800 / 446;
  object-fit: cover;
}

.product-result__actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.85rem;
}
.product-result__choose {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1rem;
  background: var(--primary);
  border: 1px solid var(--primary);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-on-primary);
  cursor: pointer;
  transition:
    background var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out),
    transform var(--dur-base) var(--ease-spring);
}
.product-result__choose:hover {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
  transform: translateY(-1px);
}
.product-result__choose:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.product-result__more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  background: none;
  border: 0;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-out);
}
.product-result__more:hover { color: var(--color-chocolate); }
.product-result__more:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
  border-radius: var(--radius-xs);
}
.product-result__chevron {
  flex-shrink: 0;
  transition: transform var(--dur-fast) var(--ease-out);
}
.product-result__chevron.is-open { transform: rotate(180deg); }

/* Détail « En savoir + » : panneau pleine largeur sous les colonnes. */
.results-detail {
  position: relative;
  /* Clears the sticky topbar + header when scrolled into view (cf. toggleDetail). */
  scroll-margin-top: calc(var(--topbar-h) + 5.5rem);
  padding: clamp(1rem, 3vw, 1.75rem);
  padding-right: 3rem;
  background: var(--bg-soft);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}
.results-detail__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--color-white);
  border: 1px solid var(--border);
  color: var(--color-chocolate);
  cursor: pointer;
  transition:
    background var(--dur-fast) var(--ease-out),
    color var(--dur-fast) var(--ease-out);
}
.results-detail__close:hover {
  background: color-mix(in oklab, var(--color-cherry) 14%, white);
  color: var(--color-cherry);
}
.results-detail__close:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
.results-detail__intro {
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}
.results-detail__summary {
  margin-top: 0.35rem;
  font-size: var(--text-base);
  line-height: var(--leading-normal);
  color: var(--color-chocolate);
  max-width: 65ch;
}
.results-detail__rows {
  margin: 1rem 0 0;
  max-width: 44rem;
}
.results-detail__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid color-mix(in oklab, var(--border) 70%, transparent);
}
.results-detail__row dt {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
}
.results-detail__row dd {
  margin: 0;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-muted);
  text-align: right;
}

/* Calaméo guarantee sheet link, at the foot of the "En savoir +" panel. */
.results-detail__sheet {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 1rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.results-detail__sheet:hover {
  color: var(--color-chocolate);
}
.results-detail__sheet:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

/* ---- Bottom sheet mobile pour le détail « en savoir + » ----
 * Desktop : le wrapper est transparent (display:contents) → le panneau reste
 * affiché inline sous la grille, comme avant. Mobile : feuille glissante
 * ancrée en bas, par-dessus un voile, scroll interne (cf. isMobileSheet, 48rem). */
.results-sheet { display: contents; }
.results-sheet__backdrop,
.results-detail__grabber { display: none; }

.results-sheet--mobile {
  display: block;
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
}
.results-sheet--mobile .results-sheet__backdrop {
  display: block;
  position: absolute;
  inset: 0;
  background: color-mix(in oklab, var(--color-chocolate) 55%, transparent);
  animation: results-sheet-fade var(--dur-base) var(--ease-out);
}
.results-sheet--mobile .results-detail {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  max-height: 85vh;
  overflow-y: auto;
  border: none;
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  padding-top: 1.25rem;
  padding-bottom: calc(1.5rem + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.18);
  animation: results-sheet-slide var(--dur-base) var(--ease-out);
}
.results-sheet--mobile .results-detail__grabber {
  display: block;
  width: 2.5rem;
  height: 0.3rem;
  margin: 0 auto 0.85rem;
  border-radius: var(--radius-pill);
  background: color-mix(in oklab, var(--color-chocolate) 25%, transparent);
}
.results-sheet--mobile .results-detail__close {
  top: 1rem;
}
@keyframes results-sheet-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes results-sheet-slide {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .results-sheet--mobile .results-sheet__backdrop,
  .results-sheet--mobile .results-detail {
    animation: none;
  }
}

</style>
